//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  PaginationState,
  getPaginationRowModel,
  VisibilityState,
  Row,
  getSortedRowModel,
  SortingState,
  RowSelectionState,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import isEqual from 'lodash.isequal';
import { useDispatch, useSelector } from 'react-redux';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  ContainerButtonStyled,
  ContainerStyled,
  ContainerLoader,
  FiltersWithLoadingStyled,
} from './styles';
import {
  Th,
  Tbody,
  Td,
  Title,
  Table,
  TextHeader,
  InputCheckContainer,
  CheckBoxStyled,
  DropDownInputStyled,
  InputCalendarStyled,
  TextCell,
} from '../components/MainStyledComponents';

// Components
import { CheckBox } from '../../../inputs/CheckBox';
import { PaginationButtons } from '../components/PaginationButtons';
import { SelectionBanner } from '../components/SelectionBanner';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { DoubleTapRow } from '../components/DoubleTapRow';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import { ExternalCompaniesCell } from '../../ExternalCompaniesCell';
import { CategoryCodeCell } from '../../CategoryCodeCell';
import { Loader } from '../../../inputs/Loader';
import InputText from '../../../inputs/InputText';
import InputBudgetCode from '../../../inputs/InputBudgetCode';
import InputCategoryCode from '../../../inputs/InputCategoryCode';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';

// Types
import {
  BudgetItemsHistoricalTableProps,
  BudgetItemHistoricalRow,
} from './types';
import {
  onChangeValue,
  Options,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputBudgetCode,
  onChangeInputBudgetCodeTypes,
} from '../../../inputs/InputBudgetCode/types';
import {
  ImperativeRefInputCategoryCode,
  onChangeInputCategoryCodeTypes,
} from '../../../inputs/InputCategoryCode/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { takeBase, tableIds } from '../types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { option } from '../InputRadioArrowTable/types';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';

// Utils
import { pkFormatter } from '../../../../utils/transform';
import { formatRoidWithDots } from '../utils/roidFormatter';

export const BudgetItemsHistoricalTable: React.FC<
  BudgetItemsHistoricalTableProps
> = ({
  idCss,
  titleText,
  data,
  onReFetchData,
  rowState,
  setRowState,
  tableId,
  onReFetchPagination,
  selectAllMode = false,
  excludedIds,
  onSelectAll,
  onClearSelection,
  rowSelection: propsRowSelection,
  onRowSelectionChange: propsOnRowSelectionChange,
  selectedCount: propsSelectedCount,
  ...props
}) => {
    // Controlled mode: parent provides rowSelection via usePersistentSelection hook
    const isControlled = propsRowSelection !== undefined;
    const { t } = useTranslation(['budgets', 'tables', 'common']);
    const dispatch = useDispatch();
    const isLandscapeTablet = useIsLandscapeTablet();
    const isPortraitTablet = useIsPortraitTablet();
    const navigate = useNavigate();

    const globalCursor = useSelector(getCursor(tableId), isEqual);
    const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);

    // Check if user has multiple concessions
    const hasMultipleConcessions = useMemo(() => {
      return (
        data.filtersDataOptions?.concessions &&
        data.filtersDataOptions.concessions.length > 1
      );
    }, [data.filtersDataOptions?.concessions]);

    // Refs para filtros
    const inputBudgetItemCodeRef = useRef<ImperativeRefInputBudgetCode>(null);
    const inputCategoryCodeRef = useRef<ImperativeRefInputCategoryCode>(null);
    const inputCategoryRef = useRef<ImperativeRefInput>(null);
    const inputConcessionRef = useRef<ImperativeRefInput>(null);
    const inputExternalCompanyRef = useRef<ImperativeRefInput>(null);
    const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
    const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
    const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
    const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
    const inputPKEndRef = useRef<ImperativeRefInputPK>(null);

    const [showFilters, setShowFilters] = useState(false);
    const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
    const rowSelection = isControlled ? propsRowSelection! : internalRowSelection;
    const setRowSelection = isControlled ? propsOnRowSelectionChange! : setInternalRowSelection;
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
    // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
    const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
      tableId as string,
    );
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: takeBase,
    });

    const handleToggleFilters = useCallback(() => {
      setShowFilters(!showFilters);
    }, [showFilters]);

    useEffect(() => {
      isLandscapeTablet ? setShowFilters(false) : setShowFilters(true);
    }, [isLandscapeTablet]);

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
      concession: !hasMultipleConcessions,
    });

    // Estado para manejar filtros locales (similar a communications)
    const [selectorData, setSelectorData] = useState<any>(null);

    // Tarea #11 — page-size selector. Activo en el listado principal de
    // Budgets historical. `liveFilters: selectorData` para respetar filtros
    // del form aún no comprometidos a Redux al cambiar el take.
    const { takePagination, paginationProps } = useServerPageSize({
      tableId,
      enabled: tableId === tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS,
      selectorCursor: globalCursor,
      liveFilters: selectorData as Record<string, unknown> | undefined,
      setCurrentServerPage,
      onReFetchData,
    });

    // Estados para valores por defecto de expediente e índice
    const [expedientDefault, setExpedientDefault] = useState<number | undefined>();
    const [indexDefault, setIndexDefault] = useState<number | undefined>();

    useLayoutEffect(() => {
      setColumnVisibility(prev => ({
        ...prev,
        concession: !hasMultipleConcessions,
      }));
    }, [hasMultipleConcessions]);

    // Transform data to table format
    const tableData = useMemo(() => {
      return (
        data.actionBudgetItems?.map(
          (item): BudgetItemHistoricalRow => ({
            id: item.id,
            categoryCode: item.budgetItem.budgetCategory.code,
            categoryColor: item.budgetItem.budgetCategory.color || null,
            budgetItemCode: item.budgetItem.code,
            description: item.budgetItem.description,
            unit: item.budgetItem.unit,
            unitPrice: item.budgetItem.unitPrice,
            quantity: item.quantity,
            totalAmount: item.totalAmount,
            registrationDate: item.action.registerInitDate || item.createdAt,
            roid: item.action.roid,
            actionTitle: item.action.actionTitle,
            pkInit: pkFormatter(item.action.pkInitKm, item.action.pkInitMeter),
            pkEnd: pkFormatter(item.action.pkEndKm, item.action.pkEndMeter),
            externalCompanies: item.action.externalCompanies,
            concession: item.action.concessions[0]?.name,
          })
        ) || []
      );
    }, [data.actionBudgetItems]);

    // Handler para Budget Item Code
    const onChangeBudgetItemCode = useCallback<onChangeInputBudgetCodeTypes>(
      (value) => {
        const dataCursor = {
          take: takePagination,
          cursor: globalCursor?.cursor ?? '',
          budgetItemCode: value,
          categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
          concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
          fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
          toDate: selectorData?.toDate ?? globalCursor?.toDate,
          indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
          indexType: selectorData?.indexType ?? globalCursor?.indexType,
          pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
          pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
          pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
          pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
          externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
          orderBy: 'desc',
        };
        dispatch(setCursor(tableId, dataCursor));
        setSelectorData((prev: any) => ({
          ...prev,
          budgetItemCode: value,
        }));
      },
      [globalCursor, selectorData, dispatch, tableId]
    );

    // Handler para Category Code
    const onChangeCategoryCode = useCallback<onChangeInputCategoryCodeTypes>(
      (value) => {
        const dataCursor = {
          take: takePagination,
          cursor: globalCursor?.cursor ?? '',
          budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
          categoryCode: value,
          concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
          fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
          toDate: selectorData?.toDate ?? globalCursor?.toDate,
          indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
          indexType: selectorData?.indexType ?? globalCursor?.indexType,
          pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
          pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
          pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
          pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
          externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
          orderBy: 'desc',
        };
        dispatch(setCursor(tableId, dataCursor));
        setSelectorData((prev: any) => ({
          ...prev,
          categoryCode: value,
        }));
      },
      [globalCursor, selectorData, dispatch, tableId]
    );

    // useEffect para inicializar expediente e índice desde cursor global
    useEffect(() => {
      setExpedientDefault(
        globalCursor?.indexExpedition !== undefined &&
          globalCursor?.indexExpedition >= 0
          ? globalCursor.indexExpedition
          : undefined
      );
      setIndexDefault(
        globalCursor?.indexType !== undefined && globalCursor?.indexType >= 0
          ? globalCursor.indexType
          : undefined
      );
    }, [globalCursor?.indexExpedition, globalCursor?.indexType]);

    // Handler para Expediente
    const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
      (value) => {
        const dataCursor = {
          take: takePagination,
          cursor: globalCursor?.cursor ?? '',
          budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
          categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
          concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
          fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
          toDate: selectorData?.toDate ?? globalCursor?.toDate,
          indexExpedition: value,
          indexType: selectorData?.indexType ?? globalCursor?.indexType,
          pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
          pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
          pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
          pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
          externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
          orderBy: 'desc',
        };
        dispatch(setCursor(tableId, dataCursor));
        setSelectorData((prev: any) => ({
          ...prev,
          indexExpedition: value,
        }));
      },
      [globalCursor, selectorData, dispatch, tableId]
    );

    // Handler para Índice
    const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
      (value) => {
        const dataCursor = {
          take: takePagination,
          cursor: globalCursor?.cursor ?? '',
          budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
          categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
          concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
          fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
          toDate: selectorData?.toDate ?? globalCursor?.toDate,
          indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
          indexType: value,
          pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
          pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
          pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
          pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
          externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
          orderBy: 'desc',
        };
        dispatch(setCursor(tableId, dataCursor));
        setSelectorData((prev: any) => ({
          ...prev,
          indexType: value,
        }));
      },
      [globalCursor, selectorData, dispatch, tableId]
    );

    // Handler para PK inicial — sin debounce: InputPK solo dispara con formato válido,
    // y esta tabla usa botón de búsqueda (no auto-search), por lo que el debounce
    // causaba race condition si el usuario hacía clic en Buscar antes de 1s.
    const onChangePKInit = useCallback<onChangeInputPKTypes>(
      (km, meter) => {
        setSelectorData((prev: any) => ({
          ...prev,
          pkInitKm: km,
          pkInitMeter: meter,
        }));
      },
      []
    );

    // Handler para PK final — sin debounce por la misma razón
    const onChangePKEnd = useCallback<onChangeInputPKTypes>(
      (km, meter) => {
        setSelectorData((prev: any) => ({
          ...prev,
          pkEndKm: km,
          pkEndMeter: meter,
        }));
      },
      []
    );

    const columnHelper = createColumnHelper<BudgetItemHistoricalRow>();

    const [sorting] = useState<SortingState>([
      { id: 'registrationDate', desc: true },
    ]);

    const columns = useMemo(() => {
      const baseColumns = [
        columnHelper.display({
          id: 'select',
          header: ({ table }) => (
            <InputCheckContainer>
              <CheckBox
                name="select"
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
              />
            </InputCheckContainer>
          ),
          cell: ({ row }) => (
            <InputCheckContainer
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <CheckBoxStyled
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </InputCheckContainer>
          ),
        }),
        columnHelper.accessor('categoryCode', {
          id: 'categoryCode',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.categoryCode')}
            </TextHeader>
          ),
          cell: (info) => (
            <CategoryCodeCell
              code={info.getValue()}
              color={info.row.original.categoryColor}
            />
          ),
          size: 120,
        }),
        columnHelper.accessor('budgetItemCode', {
          id: 'budgetItemCode',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.budgetItemCode')}
            </TextHeader>
          ),
          cell: (info) => <TextCell size="S">{info.getValue()}</TextCell>,
          size: 150,
        }),
        columnHelper.accessor('roid', {
          id: 'roid',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.roid')}
            </TextHeader>
          ),
          cell: (info) => <TextCell size="S">{formatRoidWithDots(info.getValue())}</TextCell>,
          size: 150,
        }),
        columnHelper.accessor('pkInit', {
          id: 'pkInit',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.pkInit')}
            </TextHeader>
          ),
          cell: (info) => <TextCell size="S">{info.getValue()}</TextCell>,
          size: 120,
        }),
        columnHelper.accessor('pkEnd', {
          id: 'pkEnd',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.pkEnd')}
            </TextHeader>
          ),
          cell: (info) => <TextCell size="S">{info.getValue()}</TextCell>,
          size: 120,
        }),
        columnHelper.accessor('registrationDate', {
          id: 'registrationDate',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.registrationDate')}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size="S">
              {format(new Date(info.getValue()), 'dd/MM/yyyy - HH:mm')}
            </TextCell>
          ),
          size: 160,
        }),
        columnHelper.accessor('description', {
          id: 'description',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.description')}
            </TextHeader>
          ),
          cell: (info) => <TextCell size="S">{info.getValue()}</TextCell>,
          size: 200,
        }),
        columnHelper.accessor('unit', {
          id: 'unit',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.unit')}
            </TextHeader>
          ),
          cell: (info) => <TextCell size="S">{info.getValue()}</TextCell>,
          size: 80,
        }),
        columnHelper.accessor('quantity', {
          id: 'quantity',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.quantity')}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size="S">{info.getValue().toFixed(2)}</TextCell>
          ),
          size: 100,
        }),
        columnHelper.accessor('unitPrice', {
          id: 'unitPrice',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.unitPrice')}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size="S">{info.getValue().toFixed(2)} €</TextCell>
          ),
          size: 120,
        }),
        columnHelper.accessor('totalAmount', {
          id: 'totalAmount',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.totalAmount')}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size="S">{info.getValue().toFixed(2)} €</TextCell>
          ),
          size: 120,
        }),
        columnHelper.accessor('externalCompanies', {
          id: 'externalCompanies',
          header: () => (
            <TextHeader size="XS" colorText="greyDark">
              {t('columns.externalCompanies')}
            </TextHeader>
          ),
          cell: (info) => <ExternalCompaniesCell companies={info.getValue()} />,
          size: 180,
        }),
      ];

      // Add concession column only if user has multiple concessions
      if (hasMultipleConcessions) {
        baseColumns.push(
          columnHelper.display({
            id: 'concession',
            header: () => (
              <TextHeader size="XS" colorText="greyDark">
                {t('columns.concession')}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCell size="S">{info.row.original.concession || '-'}</TextCell>
            ),
            size: 150,
          })
        );
      }

      return baseColumns;
    }, [t, hasMultipleConcessions]);

    // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
    const pagination = useMemo(
      () => ({
        pageIndex,
        pageSize: takePagination,
      }),
      [pageIndex, takePagination]
    );

    const table = useReactTable({
      data: tableData,
      columns,
      getRowId: (row) => row.id, // Use the actual ID from the data
      state: {
        rowSelection,
        columnFilters,
        pagination,
        columnVisibility,
        sorting,
      },
      getSortedRowModel: getSortedRowModel(),
      onColumnVisibilityChange: setColumnVisibility,
      enableRowSelection: true,
      enableMultiRowSelection: true,
      onPaginationChange: setPagination,
      onRowSelectionChange: setRowSelection,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      getPaginationRowModel: getPaginationRowModel(),
    });

    // Flag to skip sync after restore (prevents infinite loop)
    const isRestoringRef = useRef(false);

    // Update rowState when selection changes (for "select all" functionality)
    useEffect(() => {
      if (isControlled) return;
      if (isRestoringRef.current) {
        isRestoringRef.current = false;
        return;
      }
      const selectedIds = Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => key)
        .filter((id) => id !== undefined);

      const currentIds = rowState.filter((id) => id !== undefined);
      const hasChanged =
        selectedIds.length !== currentIds.length ||
        !selectedIds.every((id) => currentIds.includes(id));

      if (hasChanged) {
        setRowState(selectedIds);
      }
    }, [rowSelection, setRowState, rowState, isControlled]);

    // Refs for persistent selection restore
    const rowStateRef = useRef(rowState);
    useEffect(() => { rowStateRef.current = rowState; }, [rowState]);
    const excludedIdsRef = useRef(excludedIds);
    useEffect(() => { excludedIdsRef.current = excludedIds; }, [excludedIds]);

    // Restore persistent selections when page data changes
    useEffect(() => {
      if (isControlled) return;
      const items = data.actionBudgetItems || [];
      const newSelection: RowSelectionState = {};
      if (selectAllMode) {
        items.forEach((row: any) => {
          if (row.id && !excludedIdsRef.current?.has(row.id)) {
            newSelection[row.id] = true;
          }
        });
      } else if (rowStateRef.current && rowStateRef.current.length > 0) {
        const persistedSet = new Set(rowStateRef.current);
        items.forEach((row: any) => {
          if (row.id && persistedSet.has(row.id)) {
            newSelection[row.id] = true;
          }
        });
      }
      isRestoringRef.current = true;
      setRowSelection(newSelection);
    }, [data.actionBudgetItems, selectAllMode, isControlled]);

    // Filter options
    const categoryOptions = useMemo(() => {
      return (
        data.filtersDataOptions?.budgetCategories?.map((category) => ({
          label: `${category.code} - ${category.name}`,
          value: category.code,
        })) || []
      );
    }, [data.filtersDataOptions?.budgetCategories]);

    const concessionOptions = useMemo(() => {
      return (
        data.filtersDataOptions?.concessions?.map((concession) => ({
          label: concession.name,
          value: concession.id,
        })) || []
      );
    }, [data.filtersDataOptions?.concessions]);

    const onChangeInput = useCallback<onChangeInputType>((range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }

      // Solo procesar si tenemos al menos una fecha válida
      if (range.from || range.to) {
        const fromDateIso = range.from ? formatISO(range.from) : undefined;
        const toDateIso = range.to ? formatISO(range.to) : undefined;
        const dataCursor = {
          take: takePagination,
          cursor: globalCursor?.cursor ?? '',
          budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
          categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
          concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
          fromDate: fromDateIso,
          toDate: toDateIso,
          indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
          indexType: selectorData?.indexType ?? globalCursor?.indexType,
          pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
          pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
          pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
          pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
          externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
          orderBy: 'desc',
        };
        dispatch(setCursor(tableId, dataCursor));
        setSelectorData((prev: any) => ({
          ...prev,
          fromDate: fromDateIso,
          toDate: toDateIso,
        }));
      }
    }, [globalCursor, selectorData, dispatch, tableId]);

    const onChangeSelectedCategory = useCallback((option: option) => {
      const dataCursor = {
        take: takePagination,
        cursor: globalCursor?.cursor ?? '',
        budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
        categoryCode: option?.value,
        concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
        toDate: selectorData?.toDate ?? globalCursor?.toDate,
        indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
        indexType: selectorData?.indexType ?? globalCursor?.indexType,
        pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
        externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
        orderBy: 'desc',
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prev: any) => ({
        ...prev,
        categoryCode: option?.value,
      }));
    }, [globalCursor, selectorData, dispatch, tableId]);

    const onChangeSelectedConcession = useCallback((option: option) => {
      const dataCursor = {
        take: takePagination,
        cursor: globalCursor?.cursor ?? '',
        budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
        categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
        concessionId: option?.value,
        fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
        toDate: selectorData?.toDate ?? globalCursor?.toDate,
        indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
        indexType: selectorData?.indexType ?? globalCursor?.indexType,
        pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
        externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
        orderBy: 'desc',
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prev: any) => ({
        ...prev,
        concessionId: option?.value,
      }));
    }, [globalCursor, selectorData, dispatch, tableId]);

    const externalCompanyOptions = useMemo(() => {
      return data.filtersDataOptions?.externalCompanies?.map(
        (company: { id: string; companyName: string }) => ({
          label: company.companyName,
          value: company.id,
        })
      ) || [];
    }, [data.filtersDataOptions?.externalCompanies]);

    const onChangeSelectedExternalCompany = useCallback((option: option) => {
      const dataCursor = {
        take: takePagination,
        cursor: globalCursor?.cursor ?? '',
        budgetItemCode: selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
        categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
        concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
        toDate: selectorData?.toDate ?? globalCursor?.toDate,
        indexExpedition: selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
        indexType: selectorData?.indexType ?? globalCursor?.indexType,
        pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
        externalCompanyId: option?.value,
        orderBy: 'desc',
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prev: any) => ({
        ...prev,
        externalCompanyId: option?.value,
      }));
    }, [globalCursor, selectorData, dispatch, tableId]);

    const handleSearch = useCallback(async () => {
      const dataCursor = {
        take: takePagination,
        cursor: '',
        budgetItemCode:
          selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
        categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
        concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
        toDate: selectorData?.toDate ?? globalCursor?.toDate,
        indexExpedition:
          selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
        indexType: selectorData?.indexType ?? globalCursor?.indexType,
        pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
        externalCompanyId: selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
        orderBy: 'desc',
      };
      dispatch(setCursor(tableId, dataCursor));
      setCurrentServerPage(1);
      onReFetchPagination();
    }, [selectorData, globalCursor, dispatch, tableId, onReFetchPagination]);

    const onClearValue = useCallback(() => {
      // Limpiar inputs usando clearSelected
      inputBudgetItemCodeRef.current?.clearSelected();
      inputCategoryCodeRef.current?.clearSelected();
      inputExpedientRef.current?.clearSelected();
      inputIndexRef.current?.clearSelected();
      inputPKInitRef.current?.clearSelected();
      inputPKEndRef.current?.clearSelected();

      inputCategoryRef.current?.clearSelected();
      inputDateRef.current?.clearSelected();
      inputExternalCompanyRef.current?.clearSelected();
      setColumnFilters([]);

      // Check if there's only one concession
      const hasOnlyOneConcession = concessionOptions && concessionOptions.length === 1;
      const onlyConcessionId = hasOnlyOneConcession ? concessionOptions[0].value : undefined;

      // If there's only one concession, keep it selected
      // Otherwise, clear it
      if (!hasOnlyOneConcession) {
        inputConcessionRef.current?.clearSelected();
      }

      // CRÍTICO: Limpiar también el estado local selectorData
      setSelectorData(null);

      const dataCursor = {
        take: takePagination,
        cursor: '',
        budgetItemCode: undefined,
        categoryCode: undefined,
        concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
        fromDate: undefined,
        toDate: undefined,
        indexExpedition: undefined,
        indexType: undefined,
        pkInitKm: undefined,
        pkInitMeter: undefined,
        pkEndKm: undefined,
        pkEndMeter: undefined,
        externalCompanyId: undefined,
        orderBy: 'desc',
      };
      dispatch(setCursor(tableId, dataCursor));
      setCurrentServerPage(1);
      onReFetchPagination();
      // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
    }, [concessionOptions, dispatch, tableId, onReFetchPagination, onChangeInputExpedient, onChangeInputIndex, takePagination]);

    // Handle row selection
    const handleClickRow = useCallback(
      (row: Row<BudgetItemHistoricalRow>) => {
        const val: boolean = row.getIsSelected();
        const newRowState = [...rowState];
        if (!val) {
          newRowState.push(row.original.id);
          setRowState(newRowState);
        } else {
          const index = newRowState.indexOf(row.original.id);
          if (index !== -1) {
            newRowState.splice(index, 1);
          }
          setRowState(newRowState);
        }
      },
      [rowState, setRowState]
    );

    // Función para construir cursor con todos los filtros (similar a communications)
    const fetchBudgetItemID = useCallback(
      async (id: string, take = takePagination) => {
        const dataSelector = {
          take,
          cursor: id,
          budgetItemCode:
            selectorData?.budgetItemCode ?? globalCursor?.budgetItemCode,
          categoryCode: selectorData?.categoryCode ?? globalCursor?.categoryCode,
          fromDate: selectorData?.fromDate ?? globalCursor?.fromDate,
          toDate: selectorData?.toDate ?? globalCursor?.toDate,
          indexExpedition:
            selectorData?.indexExpedition ?? globalCursor?.indexExpedition,
          indexType: selectorData?.indexType ?? globalCursor?.indexType,
          concessionId: selectorData?.concessionId ?? globalCursor?.concessionId,
          pkInitKm: selectorData?.pkInitKm ?? globalCursor?.pkInitKm,
          pkInitMeter: selectorData?.pkInitMeter ?? globalCursor?.pkInitMeter,
          pkEndKm: selectorData?.pkEndKm ?? globalCursor?.pkEndKm,
          pkEndMeter: selectorData?.pkEndMeter ?? globalCursor?.pkEndMeter,
          externalCompanyId:
            selectorData?.externalCompanyId ?? globalCursor?.externalCompanyId,
          orderBy: 'desc',
        };

        dispatch(setCursor(tableId, dataSelector));
        return dataSelector;
      },
      [globalCursor, selectorData, dispatch, tableId, takePagination]
    );

    // Pagination handlers
    const nextPagination = useCallback(async () => {
      // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
      const pageCount = Math.max(
        1,
        Math.ceil((data?.totalCount ?? 0) / takePagination),
      );
      if (
        data.actionBudgetItems?.length >= 1 &&
        data.pageInfo?.hasNextPage &&
        currentServerPage < pageCount
      ) {
        setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
        await fetchBudgetItemID(
          data.actionBudgetItems[data.actionBudgetItems.length - 1].id
        );
        onReFetchPagination();
      }
    }, [
      data.actionBudgetItems,
      data.pageInfo,
      data?.totalCount,
      fetchBudgetItemID,
      onReFetchPagination,
      takePagination,
      currentServerPage,
    ]);

    const backPagination = useCallback(async () => {
      if (data.actionBudgetItems?.length >= 1 && data.pageInfo?.hasPreviousPage) {
        setCurrentServerPage((prev) => Math.max(1, prev - 1));
        await fetchBudgetItemID(data.actionBudgetItems[0].id, -takePagination);
        onReFetchPagination();
      }
    }, [
      data.actionBudgetItems,
      data.pageInfo,
      fetchBudgetItemID,
      onReFetchPagination,
      takePagination,
    ]);

    // Update row selection state (only in uncontrolled mode)
    useEffect(() => {
      if (isControlled) return;
      const newSelection: Record<string, boolean> = {};
      rowState.forEach((id) => {
        if (id) newSelection[id] = true;
      });
      setRowSelection(newSelection);
    }, [rowState, isControlled]);

    const hiddenColumns: number = Object.values(
      columnVisibility as object
    ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

    const commonColumns = table.getAllColumns().slice(1);
    // commonLabels definition removed

    const filterContainerRef = useRef<HTMLDivElement>(null);
    useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

    return (
      <ContainerStyled>
        {isLoadingData && (
          <ContainerLoader>
            <Loader />
          </ContainerLoader>
        )}
        <FiltersWithLoadingStyled isLoading={isLoadingData}>
          <SubContainerHead>
            <Title component="h2" variant="semiBold" size="M">
              {titleText}
            </Title>
            {isLandscapeTablet && (
              <ShowFiltersButton
                isBlack={showFilters}
                onClick={handleToggleFilters}
              >
                <ArrowBottomStyled isRotate={showFilters} />
              </ShowFiltersButton>
            )}
          </SubContainerHead>
          {showFilters && (
            <SubContainerFiltersStyled ref={filterContainerRef}>
              <InputCategoryCode
                ref={inputCategoryCodeRef}
                label={t('filters.categoryCode')}
                disabled={isLoadingData}
                categoryCode={globalCursor?.categoryCode}
                onChangeInputCategoryCode={onChangeCategoryCode}
              />

              <InputBudgetCode
                ref={inputBudgetItemCodeRef}
                label={t('filters.budgetItemCode')}
                disabled={isLoadingData}
                budgetCode={globalCursor?.budgetItemCode}
                onChangeInputBudgetCode={onChangeBudgetItemCode}
              />

              <InputExpedient
                ref={inputExpedientRef}
                label={t('filters.expedient')}
                disabled={isLoadingData}
                expedient={expedientDefault}
                onChangeInputExpedient={onChangeInputExpedient}
              />

              <InputIndex
                ref={inputIndexRef}
                label={t('filters.index')}
                disabled={isLoadingData}
                index={indexDefault}
                onChangeInputIndex={onChangeInputIndex}
              />

              <InputPK
                ref={inputPKInitRef}
                label={t('tables:headers.pkInit')}
                disabled={isLoadingData}
                onChangeInputPK={onChangePKInit}
              />

              <InputPK
                ref={inputPKEndRef}
                label={t('tables:headers.pkEnd')}
                disabled={isLoadingData}
                onChangeInputPK={onChangePKEnd}
              />

              <InputCalendarStyled
                ref={inputDateRef}
                label={t('filters.dateRange')}
                name="dateRange"
                onChangeInput={onChangeInput}
                dateRange={
                  globalCursor?.fromDate && globalCursor?.toDate
                    ? {
                      from: parseISO(globalCursor?.fromDate),
                      to: parseISO(globalCursor?.toDate),
                    }
                    : undefined
                }
                disabled={isLoadingData}
              />

              <DropDownInputStyled
                inputSize="L"
                ref={inputCategoryRef}
                labelText={t('filters.category')}
                placeholder={t('common:placeholders.select')}
                typeDropDown="Secondary"
                defaultValue={globalCursor?.categoryCode ?? ''}
                options={categoryOptions as Options}
                onChangeSelected={onChangeSelectedCategory as onChangeValue}
                isDisabled={isLoadingData}
              />

              {hasMultipleConcessions && (
                <DropDownInputStyled
                  inputSize="XS"
                  ref={inputConcessionRef}
                  labelText={t('filters.concession')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown="Secondary"
                  defaultValue={globalCursor?.concessionId ?? ''}
                  options={concessionOptions as Options}
                  onChangeSelected={onChangeSelectedConcession as onChangeValue}
                  isDisabled={isLoadingData}
                />
              )}

              {externalCompanyOptions.length > 0 && (
                <DropDownInputStyled
                  inputSize="L"
                  ref={inputExternalCompanyRef}
                  labelText={t('common:tables.headers.externalCompanies')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown="Secondary"
                  defaultValue={globalCursor?.externalCompanyId ?? ''}
                  options={externalCompanyOptions as Options}
                  onChangeSelected={onChangeSelectedExternalCompany as onChangeValue}
                  isDisabled={isLoadingData}
                />
              )}

              <ContainerButtonStyled>
                <ButtonIconBackground
                  variant="delete"
                  onClick={onClearValue}
                  disabled={isLoadingData}
                  title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
                />
                <DropDownVisibilityColumn
                  columnNames={Object.fromEntries(table.getAllColumns().slice(1).map(c => [c.id, typeof c.columnDef.header === 'string' ? c.columnDef.header : t(`columns.${c.id}`) || c.id]))}
                  columns={commonColumns as any}
                  isDisabled={isLoadingData}
                />
                <ButtonIconBackground
                  variant="restart"
                  onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                  disabled={isLoadingData}
                />
                <ButtonIconBackground
                  variant="search"
                  onClick={handleSearch}
                  disabled={isLoadingData}
                  title={`${t('buttons:search')} (Enter)`}
                />
              </ContainerButtonStyled>
            </SubContainerFiltersStyled>
          )}
        </FiltersWithLoadingStyled>
        <TableContainerStyled isLoading={isLoadingData}>
          <Table {...props} id={idCss}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </Th>
                  ))}
                </tr>
              ))}
            </thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    !isLoadingData && row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    if (row.original?.id) {
                      navigate(`summary/${row.original.id}`);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      hiddenColumns={hiddenColumns}
                      column={cell.column.id as any}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </DoubleTapRow>
              ))}
            </Tbody>
          </Table>
        </TableContainerStyled>
        {onSelectAll && onClearSelection && (
          <SelectionBanner
            selectedCount={
              isControlled
                ? (propsSelectedCount ?? 0)
                : selectAllMode
                  ? (data?.totalCount || 0) - (excludedIds?.size || 0)
                  : Object.keys(rowSelection).filter((k) => rowSelection[k]).length
            }
            totalCount={data?.totalCount || 0}
            pageCount={(data.actionBudgetItems || []).length}
            allPageSelected={
              (data.actionBudgetItems || []).length > 0 &&
              (data.actionBudgetItems || []).every((row: any) => rowSelection[row.id])
            }
            selectAllMode={selectAllMode}
            onSelectAll={onSelectAll}
            onClearSelection={onClearSelection}
          />
        )}
        <PaginationButtons
          pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
          currentPage={currentServerPage}
          nextPage={nextPagination}
          previousPage={backPagination}
          totalCount={data?.totalCount ?? 0}
          isDisabled={isLoadingData}
          // Tarea #11: gates aplicados en `useServerPageSize`. En tablet
          // o tableId no-principal, props undefined.
          {...paginationProps}
        />
      </ContainerStyled>
    );
  };
